import clsx from 'clsx';
import CarrouselBase from 'core/modules/components/sliders/components/Carrousel';
import React from 'react';
import { FreeMode, Keyboard, Navigation, Thumbs } from 'swiper';
import type { Swiper as SwiperInstance, SwiperOptions } from 'swiper';
import Icon from '../../../atoms/Icon/Icon';
import Image from '../../../atoms/Image/Image';
import Dialog from '../Dialog/Dialog';
import useDialogCarrouselImages from './useDialogCarrouselImages';

/**
 * DialogCarrouselImages — v2 organism.
 *
 * Modal full-screen para previsualizar una galeria de imagenes. Compone
 * {@link Dialog} con `size="all-screen"` e `isHeaderHidden`, anade un
 * boton de cierre flotante propio y un carrusel principal con tira de
 * thumbnails sincronizada (basado en Swiper).
 *
 * Accesibilidad (WCAG 2.1 AA):
 *  - Cada recurso requiere `alt` (WCAG 1.1.1). El consumidor pre-traduce
 *    el texto alternativo. Si la imagen es decorativa, pasa `alt=""`
 *    explicitamente como opt-in valido.
 *  - El dialog hereda focus trap, cierre por ESC y `aria-modal`.
 *  - `ariaLabel` da nombre accesible al dialog; si no se pasa, se usa
 *    el fallback generico `t('common:Image gallery')`.
 *  - El carrusel principal habilita navegacion con teclado (modulo
 *    `Keyboard` de Swiper) ademas de los botones de prev/next visibles.
 *  - El boton de cierre tiene `aria-label={t('actions:Close')}` y foco
 *    visible.
 */

type Resource = Readonly<{
  alt: string;
  content: Readonly<{ guid: string; name: string; url: string }>;
}>;

type DialogCarrouselImagesProps = Readonly<{
  ariaLabel?: string;
  currentResource: Resource;
  isOpen: boolean;
  onClose: () => void;
  onSlideChange?: (swiper: SwiperInstance) => void;
  resources: ReadonlyArray<Resource>;
}>;

type CarrouselProps = Readonly<{
  className?: string;
  initialSlide?: number;
  items: ReadonlyArray<React.ReactNode>;
  modules?: SwiperOptions['modules'];
  onSlideChange?: (swiper: SwiperInstance) => void;
  onSwiper?: (swiper: SwiperInstance | null) => void;
  style?: React.CSSProperties;
}> &
  Partial<Omit<SwiperOptions, 'modules'>>;

const Carrousel = CarrouselBase as unknown as React.ComponentType<CarrouselProps>;

function DialogCarrouselImages({
  ariaLabel,
  currentResource,
  isOpen,
  onClose,
  onSlideChange,
  resources,
}: DialogCarrouselImagesProps): React.JSX.Element {
  const {
    activeIndex,
    closeLabel,
    handleSlideChange,
    initialIndex,
    resolvedAriaLabel,
    setThumbsSwiper,
    thumbsSwiper,
  } = useDialogCarrouselImages({ ariaLabel, currentResource, isOpen, onSlideChange, resources });

  const mainItems = resources.map((resource) => (
    <div className="dialog-carrousel-images__image-container" key={resource.content.guid}>
      <Image alt={resource.alt} className="dialog-carrousel-images__image" src={resource.content.url} />
    </div>
  ));

  const thumbnailItems = resources.map((resource, index) => (
    <div
      className={clsx('dialog-carrousel-images__thumbnail', {
        'dialog-carrousel-images__thumbnail--active': index === activeIndex,
      })}
      key={resource.content.guid}
      style={{ backgroundImage: `url(${encodeURI(resource.content.url)})` }}
    />
  ));

  return (
    <Dialog
      ariaLabel={resolvedAriaLabel}
      className="dialog-carrousel-images-dialog"
      isHeaderHidden
      isOpen={isOpen}
      onClose={onClose}
      size="all-screen"
    >
      <div className="dialog-carrousel-images">
        <button aria-label={closeLabel} className="dialog-carrousel-images__close" onClick={onClose} type="button">
          <Icon name="close" />
        </button>
        <div className="dialog-carrousel-images__main">
          <Carrousel
            className="dialog-carrousel-images__main-swiper"
            initialSlide={initialIndex}
            items={mainItems}
            keyboard={{ enabled: true }}
            modules={[FreeMode, Keyboard, Navigation, Thumbs]}
            navigation
            onSlideChange={handleSlideChange}
            slidesPerView={1}
            spaceBetween={10}
            thumbs={{ swiper: thumbsSwiper }}
          />
        </div>
        <div className="dialog-carrousel-images__thumbnails">
          <Carrousel
            className="dialog-carrousel-images__thumbnails-swiper"
            freeMode
            items={thumbnailItems}
            modules={[FreeMode, Navigation]}
            navigation={false}
            onSwiper={setThumbsSwiper}
            slidesPerView="auto"
            spaceBetween={10}
            watchSlidesProgress
          />
        </div>
      </div>
    </Dialog>
  );
}

export default DialogCarrouselImages;
